import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiHttpError } from "@atlas/sdk";
import { Button, Modal, TextareaField } from "@atlas/ui/web";
import { atlas } from "../lib/sdk";
import { mensagemDeErro } from "../lib/erro-validacao";

/** Desaverba por liquidação: confirmação + motivo livre obrigatório. */
export function DesaverbarContratoModal({
  adf,
  nome,
  onClose,
  onFeito,
}: {
  adf: string;
  nome: string;
  onClose: () => void;
  onFeito: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const qc = useQueryClient();
  const previa = useQuery({
    queryKey: ["banco", "previa-desaverbacao", adf],
    queryFn: () => atlas.banco.previaDesaverbacao(adf),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    networkMode: "always",
    retry: false,
  });
  const acessoNegado = previa.error instanceof ApiHttpError && [401, 403, 404].includes(previa.error.status);
  const dados = previa.isError ? undefined : previa.data;
  const mut = useMutation({
    mutationFn: () => atlas.banco.acao(adf, "quitar", { motivo: motivo.trim() }),
    onSuccess: () => {
      for (const key of ["contratos-api", "contratos", "margem", "previa-desaverbacao"]) {
        void qc.invalidateQueries({ queryKey: ["banco", key] });
      }
      onFeito();
    },
  });
  const motivoOk = motivo.trim().length >= 3;
  const erro = mut.isError ? mensagemDeErro(mut.error, "Não foi possível desaverbar.") : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      danger
      eyebrow="Liquidação"
      title="Desaverbar contrato"
      subtitle={`${nome} · ${adf}`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            style={{ whiteSpace: "normal", textAlign: "center" }}
            onClick={() => mut.mutate()}
            disabled={!motivoOk || mut.isPending || previa.isFetching || previa.isPending || acessoNegado}
          >
            {mut.isPending ? "Desaverbando…" : "Desaverbar — sem reversão na plataforma"}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 14, color: "var(--text-muted)", lineHeight: 1.45 }}>
        O contrato vai para <strong>Quitado</strong> e{" "}
        <strong>não poderá ser reativado pela plataforma</strong>. O desconto em folha{" "}
        <strong>deste mês não volta</strong> — a instrução para na <strong>próxima competência</strong>.
      </p>
      {previa.isFetching ? <p role="status">Consultando a margem…</p> : null}
      {previa.isError ? (
        <div role="alert">
          <p>
            {acessoNegado
              ? mensagemDeErro(previa.error, "Sem acesso a este contrato.")
              : "Não foi possível consultar a margem. A projeção está indisponível; você ainda pode confirmar uma liquidação legítima."}
          </p>
          <Button
            type="button"
            variant="ghost"
            disabled={previa.isFetching || mut.isPending}
            onClick={() => void previa.refetch()}
          >
            Tentar novamente
          </Button>
        </div>
      ) : null}
      {dados ? (
        <section aria-label="Prévia da margem">
          <p>
            <strong>
              {dados.tipoMargem === "EMPRESTIMO"
                ? "Empréstimo"
                : dados.tipoMargem === "CARTAO_CONSIGNADO"
                  ? "Cartão consignado"
                  : "Cartão de benefícios"}
            </strong>
          </p>
          <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px 16px" }}>
            {(
              [
                ["Margem total", dados.total],
                ["Utilizado", dados.utilizado],
                ["Saldo atual", dados.saldoAtual],
                ["Parcela do contrato", dados.valorParcela],
                ["Saldo previsto após desaverbar", dados.saldoAposDesaverbacao],
              ] as const
            ).map(([label, valor]) => (
              <LinhaMargem key={label} label={label} valor={valor} />
            ))}
          </dl>
          {dados.saldoAtual != null && dados.saldoAtual < 0 ? (
            <p role="alert" style={{ color: "var(--danger-500)", fontWeight: 600 }}>
              A margem atual está negativa. As parcelas já excedem a margem do servidor.
            </p>
          ) : null}
          {dados.saldoAposDesaverbacao != null && dados.saldoAposDesaverbacao <= 0 ? (
            <p role="alert" style={{ color: "var(--danger-500)", fontWeight: 600 }}>
              Mesmo após desaverbar, o servidor continuará sem margem disponível. Um contrato novo não poderá
              ser averbado com esse saldo.
            </p>
          ) : null}
          {dados.liberacao === "aguarda_folha" ? (
            <p role="alert">
              A desaverbação não libera imediatamente a margem. É necessário aguardar a retirada do contrato
              ser conciliada na folha.
            </p>
          ) : null}
          {dados.liberacao === "indeterminada" ? (
            <p role="alert">
              Não é possível afirmar a margem que ficará disponível. Há impedimentos ou dados insuficientes
              para a projeção.
            </p>
          ) : null}
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            Prévia informativa. Uma nova contratação depende da margem e das demais condições no momento da
            averbação.
          </p>
        </section>
      ) : null}
      <p>
        Para renovar um contrato, utilize o menu <strong>Refinanciamento</strong>. Nesse fluxo, somente a
        diferença de parcela consome margem.
      </p>
      <TextareaField
        label="Motivo"
        required
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        hint="Texto livre. Mínimo 3 caracteres."
        error={erro ?? undefined}
        placeholder="Ex.: liquidação antecipada na agência em 29/09/2026"
      />
    </Modal>
  );
}

function LinhaMargem({ label, valor }: { label: string; valor: number | null }) {
  return (
    <>
      <dt>{label}</dt>
      <dd
        style={{
          margin: 0,
          fontWeight: 600,
          color: valor != null && valor < 0 ? "var(--danger-500)" : undefined,
        }}
      >
        {valor == null
          ? "Indisponível"
          : valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
      </dd>
    </>
  );
}
